import * as StellarSdk from '@stellar/stellar-sdk';
import { Keypair, rpc, xdr } from '@stellar/stellar-sdk';

import { BASE_FEE } from 'constants/stellar';

import { getNetworkPassphrase } from 'helpers/env';
import {
    KnownPrepareErrors,
    SorobanErrorHandler,
    SorobanPrepareTxErrorHandler,
} from 'helpers/error-handler';
import { getErrorMessage, getTxSentryExtra } from 'helpers/sentry';
import { retryTransactionSubmit } from 'helpers/transaction-retry';
import { getSorobanUrl } from 'helpers/url';

import { ModalService } from 'services/globalServices';
import SentryService from 'services/sentry.service';

// Expected/transient submit failures we don't want to alert on in Sentry.
const NOISY_SUBMIT_ERROR_MESSAGES = new Set<string>([
    'Try again later',
    'Transaction failed because of timeout. Please make another attempt.',
]);

export default class Connection {
    private server: StellarSdk.rpc.Server | null = null;
    private keypair: Keypair | null = null;

    constructor() {
        this.startServer();
    }

    startServer(): void {
        this.server = new rpc.Server(getSorobanUrl());
    }

    loginWithSecret(secretKey: string): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                this.keypair = StellarSdk.Keypair.fromSecret(secretKey);

                resolve(this.keypair.publicKey());
            } catch (e) {
                reject(e);
            }
        });
    }

    logoutWithSecret() {
        if (this.keypair) {
            this.keypair = null;
        }
    }

    pollTx<T>(
        hash: string,
    ): Promise<
        T | rpc.Api.GetFailedTransactionResponse | rpc.Api.GetMissingTransactionResponse | xdr.ScVal
    > {
        return this.server
            .pollTransaction(hash, {
                attempts: 30,
                sleepStrategy: () => 2000,
            })
            .then(res => {
                if (res.status === 'SUCCESS') {
                    return res.returnValue;
                }
                throw new Error(
                    'Transaction failed because of timeout. Please make another attempt.',
                );
            });
    }

    processResponse(response: rpc.Api.SendTransactionResponse) {
        if (response.status === 'TRY_AGAIN_LATER') {
            throw new Error('Try again later');
        }
        if (response.status === 'DUPLICATE') {
            return this.pollTx(response.hash);
        }
        if (response.status !== 'PENDING') {
            throw new Error(SorobanErrorHandler(response.errorResult?.result.type ?? 'unknown'));
        }
        return this.pollTx(response.hash);
    }

    async tryRestore(tx: StellarSdk.Transaction) {
        let sim: rpc.Api.SimulateTransactionResponse;

        try {
            sim = await this.server.simulateTransaction(tx);
        } catch (error) {
            SentryService.captureException({
                error,
                extra: getTxSentryExtra(tx, error),
                prefix: 'Soroban transaction simulation failed',
            });
            throw error;
        }

        // Simulation-error responses are intentionally passed through here —
        // server.prepareTransaction() further in the chain will surface a
        // user-friendly message via SorobanPrepareTxErrorHandler.

        if (!(sim as StellarSdk.rpc.Api.SimulateTransactionRestoreResponse).restorePreamble) {
            return Promise.resolve();
        }

        const account = await this.server.getAccount(tx.source);
        let fee = parseInt(BASE_FEE);

        fee += parseInt(
            (sim as StellarSdk.rpc.Api.SimulateTransactionRestoreResponse).restorePreamble
                .minResourceFee,
        );

        const restoreTx = new StellarSdk.TransactionBuilder(account, { fee: fee.toString() })
            .setNetworkPassphrase(getNetworkPassphrase())
            .setSorobanData(
                (
                    sim as StellarSdk.rpc.Api.SimulateTransactionRestoreResponse
                ).restorePreamble.transactionData.build(),
            )
            .addOperation(StellarSdk.Operation.restoreFootprint({}))
            .setTimeout(StellarSdk.TimeoutInfinite)
            .build();

        const { default: RestoreContractModal } = await import('web/modals/RestoreContractModal');
        ModalService.openModal(RestoreContractModal, { tx: restoreTx });

        return Promise.reject({ message: 'Something expired' });
    }

    async buildSmartContractTx(
        publicKey: string,
        contractId: string,
        method: string,
        ...args: xdr.ScVal[]
    ) {
        const acc = await this.server.getAccount(publicKey);
        const contract = new StellarSdk.Contract(contractId);
        const builtTx = new StellarSdk.TransactionBuilder(acc, {
            fee: BASE_FEE,
            networkPassphrase: getNetworkPassphrase(),
        });
        if (args) {
            builtTx.addOperation(contract.call(method, ...args));
        } else {
            builtTx.addOperation(contract.call(method));
        }
        return builtTx.setTimeout(StellarSdk.TimeoutInfinite).build();
    }

    /**
     * Builds a transaction intended for simulation only (read-only contract
     * calls). The source account is constructed locally with a zero sequence
     * number: simulateTransaction doesn't validate the sequence, so this
     * avoids the extra getAccount RPC round-trip per read.
     * Never use the result for signing/submitting — the sequence is fake.
     */
    buildSmartContractTxForSimulate(
        publicKey: string,
        contractId: string,
        method: string,
        ...args: xdr.ScVal[]
    ): Promise<StellarSdk.Transaction> {
        const acc = new StellarSdk.Account(publicKey, '0');
        const contract = new StellarSdk.Contract(contractId);
        const builtTx = new StellarSdk.TransactionBuilder(acc, {
            fee: BASE_FEE,
            networkPassphrase: getNetworkPassphrase(),
        });
        if (args) {
            builtTx.addOperation(contract.call(method, ...args));
        } else {
            builtTx.addOperation(contract.call(method));
        }
        return Promise.resolve(builtTx.setTimeout(StellarSdk.TimeoutInfinite).build());
    }

    buildSmartContractTxFromOp(publicKey, operation) {
        return this.server.getAccount(publicKey).then(acc => {
            const builtTx = new StellarSdk.TransactionBuilder(acc, {
                fee: BASE_FEE,
                networkPassphrase: getNetworkPassphrase(),
            });

            builtTx.addOperation(operation);

            return builtTx.setTimeout(StellarSdk.TimeoutInfinite).build();
        });
    }

    signWithSecret(tx: StellarSdk.Transaction) {
        tx.sign(this.keypair);
        return tx;
    }

    getAccount(publicKey: string) {
        return this.server.getAccount(publicKey);
    }

    async submitTx(tx: StellarSdk.Transaction) {
        let response: rpc.Api.SendTransactionResponse | undefined;

        try {
            return await retryTransactionSubmit(async () => {
                response = await this.server.sendTransaction(tx);
                return this.processResponse(response);
            });
        } catch (error) {
            // Skip retry/timeout cases — these are expected and would just create noise.
            if (!NOISY_SUBMIT_ERROR_MESSAGES.has(getErrorMessage(error) ?? '')) {
                SentryService.captureException({
                    error,
                    extra: {
                        ...getTxSentryExtra(tx, error),
                        responseHash: response?.hash,
                        responseStatus: response?.status,
                    },
                    prefix: 'Soroban transaction submit failed',
                });
            }

            throw error;
        }
    }

    async simulateTx(
        tx: StellarSdk.Transaction,
        // silent: don't report simulation errors to Sentry. Used by callers
        // that recover from expected failures themselves (e.g. batch reads
        // bisected on Budget/ExceededLimit).
        { silent = false }: { silent?: boolean } = {},
    ): Promise<StellarSdk.rpc.Api.SimulateTransactionSuccessResponse> {
        let response: rpc.Api.SimulateTransactionResponse;

        try {
            response = await this.server.simulateTransaction(tx);
        } catch (error) {
            if (!silent) {
                SentryService.captureException({
                    error,
                    extra: getTxSentryExtra(tx, error),
                    prefix: 'Soroban transaction simulation failed',
                });
            }
            throw error;
        }

        if (rpc.Api.isSimulationError(response) && !silent) {
            SentryService.captureException({
                error: response.error,
                extra: {
                    ...getTxSentryExtra(tx, response.error),
                    latestLedger: response.latestLedger,
                },
                prefix: 'Soroban transaction simulation failed',
            });
        }

        return response as StellarSdk.rpc.Api.SimulateTransactionSuccessResponse;
    }

    prepareTransaction(tx: StellarSdk.Transaction) {
        return this.tryRestore(tx).then(() =>
            this.server.prepareTransaction(tx).catch(err => {
                SentryService.captureException({
                    error: err,
                    extra: getTxSentryExtra(tx, err),
                    prefix: 'Soroban transaction prepare failed',
                });
                throw SorobanPrepareTxErrorHandler(err);
            }),
        );
    }

    async prepareSdkTransaction(build: () => Promise<string>): Promise<StellarSdk.Transaction> {
        try {
            return new StellarSdk.Transaction(await build(), getNetworkPassphrase());
        } catch (error) {
            const { RestoreRequiredError } = await import('@aquariusdefi/sdk');
            if (error instanceof RestoreRequiredError) {
                const { default: RestoreContractModal } =
                    await import('web/modals/RestoreContractModal');
                ModalService.openModal(RestoreContractModal, {
                    tx: new StellarSdk.Transaction(
                        error.restoreTransactionXdr,
                        getNetworkPassphrase(),
                    ),
                });
                throw new Error('Something expired');
            }
            SentryService.captureException({
                error,
                prefix: 'Aquarius SDK transaction prepare failed',
                extra: {},
            });
            if (Object.keys(KnownPrepareErrors).some(code => String(error).includes(code))) {
                throw new Error(SorobanPrepareTxErrorHandler(String(error)));
            }
            throw error;
        }
    }

    getLedgerEntries(...args: xdr.LedgerKey[]) {
        return this.server.getLedgerEntries(...args);
    }
}
